import { useEffect, useRef, useState } from 'react';
import { s } from './Pattern.styles';
import {
  auctionDateYearsOptions,
  investmentTypeOptions,
  listTypeOptions,
  prcReliabilityOptions,
  profitRateOptions,
  profitStatusOptions,
  revenueTypeOptions,
  riskSelectOptions,
  valueStatusOptions,
} from '../data';
import { usePatternContext } from '../../../../context/PatternContext';
import RiskFactorModal from './RiskFactorModal';
import PurposeModal from './PurposeModal';
import SelectedOptions from './SelectedOptions';
import DiscountModal from './DiscountModal';
import CostIncludeModal from './CostIncludeModal';

const CorePattern = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [isMinPriceFocus, setIsMinPriceFocus] = useState(false);
  const [isMaxPriceFocus, setIsMaxPriceFocus] = useState(false);
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [costIncludeModalOpen, setCostIncludeModalOpen] = useState(false);

  const [purposeModalOpen, setPurposeModalOpen] = useState(false); // 모달 On/Off

  const { profitOptions, setProfitOptions } = usePatternContext();

  const calculatePrice = (price: string) => {
    const value = Number(price);

    if (value >= 10000) {
      const eok = (value / 10000).toFixed(0);
      return `${parseFloat(eok)}억원`;
    } else {
      return `${value}만원`;
    }
  };

  const toggleInvestmentType = (value: string) => {
    setProfitOptions((prev) => ({
      ...prev,
      investmentType: value,
      investmentRate: value === 'A' ? '' : prev.investmentRate,
    }));
  };

  const riskAllValues = riskSelectOptions
    .filter((opt) => opt.value !== 'all')
    .map((opt) => opt.value);

  return (
    <s.PatternBox>
      <s.SelectHeader onClick={() => setIsOpen((prev) => !prev)}>
        <s.SelectTitleWrapper>
          <s.SelectTitle>수익 알고리즘</s.SelectTitle>
          <s.SelectHeaderStep>Step 2.</s.SelectHeaderStep>
        </s.SelectTitleWrapper>
        <s.ArrowButton
          $isOpen={isOpen}
          src={'/assets/images/arrow/arrow-up-black.svg'}
          alt=""
        />
      </s.SelectHeader>
      {isOpen && (
        <>
          <s.Divider />
          <s.SelectMain>
            <s.Block>
              <s.BlockLabel>
                <span>수익 유형</span>
              </s.BlockLabel>
              <s.ColumnBox>
                <s.BlockContent>
                  {revenueTypeOptions.map((option) => {
                    const isDisabled = option.value === 'B';

                    return (
                      <s.RadioButtonLabel02
                        key={option.value}
                        checked={profitOptions.revenueType === option.value}
                        disabled={isDisabled}
                        style={{ height: '40px' }}
                      >
                        <input
                          type="radio"
                          name="revenue-type"
                          value={option.value}
                          checked={profitOptions.revenueType === option.value}
                          disabled={isDisabled}
                          onChange={() =>
                            !isDisabled &&
                            setProfitOptions({
                              ...profitOptions,
                              revenueType: option.value,
                            })
                          }
                        />
                        {option.label}
                      </s.RadioButtonLabel02>
                    );
                  })}
                </s.BlockContent>
                <s.CheckButtonLabel>
                  <input
                    type="checkbox"
                    checked={profitOptions.costIncluded === 'Y'}
                    onChange={() =>
                      setProfitOptions({
                        ...profitOptions,
                        costIncluded:
                          profitOptions.costIncluded === 'Y' ? 'N' : 'Y',
                      })
                    }
                  />
                  <img
                    src={
                      profitOptions.costIncluded === 'Y'
                        ? '/assets/images/icon/algo-check-icon-on.svg'
                        : '/assets/images/icon/algo-check-icon-off.svg'
                    }
                    alt="부대비용 포함 여부"
                  />
                  부대비용 포함
                  <img
                    src="/assets/images/icon/help.svg"
                    alt=""
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCostIncludeModalOpen(true);
                    }}
                  />
                </s.CheckButtonLabel>
              </s.ColumnBox>
            </s.Block>
            <s.Block>
              <s.BlockLabel>
                <span>전체 투자금</span>
              </s.BlockLabel>
              <s.FilterRowContent>
                <s.BlockContent style={{ paddingBottom: '24px' }}>
                  <s.InputBox>
                    <s.NumberInput
                      type="number"
                      pattern="[0-9]*"
                      placeholder="최소"
                      value={profitOptions.minInvestmentValue}
                      onFocus={() => {
                        setIsMinPriceFocus(true);
                      }}
                      onBlur={() => setIsMinPriceFocus(false)}
                      onChange={(e) => {
                        // 입력값에서 숫자만 추출
                        let rawValue = e.target.value.replace(/[^0-9]/g, '');
                        if (rawValue.length > 9)
                          rawValue = rawValue.slice(0, 8);
                        setProfitOptions({
                          ...profitOptions,
                          minInvestmentValue: rawValue,
                        });
                      }}
                    />
                    <span style={{ whiteSpace: 'nowrap' }}>만원</span>
                    <s.InputDescription>
                      {!isMinPriceFocus &&
                        profitOptions.minInvestmentValue &&
                        `${calculatePrice(profitOptions.minInvestmentValue)}`}
                    </s.InputDescription>
                  </s.InputBox>
                  <span style={{ padding: '0 4px' }}>~</span>
                  <s.InputBox>
                    <s.NumberInput
                      type="number"
                      pattern="[0-9]*"
                      placeholder="최대"
                      value={profitOptions.maxInvestmentValue}
                      onFocus={() => {
                        setIsMaxPriceFocus(true);
                      }}
                      onBlur={() => setIsMaxPriceFocus(false)}
                      onChange={(e) => {
                        // 입력값에서 숫자만 추출
                        let rawValue = e.target.value.replace(/[^0-9]/g, '');
                        if (rawValue.length > 9)
                          rawValue = rawValue.slice(0, 8);
                        setProfitOptions({
                          ...profitOptions,
                          maxInvestmentValue: rawValue,
                        });
                      }}
                    />
                    <span style={{ whiteSpace: 'nowrap' }}>만원</span>
                    <s.InputDescription>
                      {!isMaxPriceFocus &&
                        profitOptions.maxInvestmentValue &&
                        `${calculatePrice(profitOptions.maxInvestmentValue)}`}
                    </s.InputDescription>
                  </s.InputBox>
                </s.BlockContent>
              </s.FilterRowContent>
            </s.Block>
            <s.Block>
              <s.BlockLabel>
                <span>투자원금/대출금</span>
              </s.BlockLabel>
              <s.ColumnBox>
                <s.BlockContent style={{ gap: '8px' }}>
                  {investmentTypeOptions.map((option) => (
                    <s.CheckButtonLabel
                      key={option.value}
                      onChange={() => toggleInvestmentType(option.value)}
                    >
                      <input
                        type="checkbox"
                        checked={profitOptions.investmentType === option.value}
                      />
                      <img
                        src={
                          profitOptions.investmentType === option.value
                            ? '/assets/images/icon/algo-cicle-icon-on.svg'
                            : '/assets/images/icon/algo-cicle-icon-off.svg'
                        }
                        alt={option.label}
                      />
                      {option.label}
                    </s.CheckButtonLabel>
                  ))}
                </s.BlockContent>
                <s.InputBox $disabled={profitOptions.investmentType === 'A'}>
                  <s.NumberInput
                    type="number"
                    pattern="[0-9]*"
                    placeholder="전체 투자금의"
                    value={profitOptions.investmentRate}
                    disabled={profitOptions.investmentType === 'A'}
                    onChange={(e) => {
                      if (profitOptions.investmentType === 'A') return;
                      let value = e.target.value.replace(/[^0-9]/g, '');
                      if (Number(value) > 100) value = '100';
                      setProfitOptions({
                        ...profitOptions,
                        investmentRate: value,
                      });
                    }}
                  />
                  <span style={{ whiteSpace: 'nowrap' }}>%</span>
                </s.InputBox>
              </s.ColumnBox>
            </s.Block>
            <s.Block>
              <s.BlockLabel>
                <span>할인 비율</span>
                <s.BlockLabelDescription>
                  할인 비율에 따라 추천물건의 유찰 횟수가 달라집니다.
                </s.BlockLabelDescription>
              </s.BlockLabel>
              <s.ColumnBox>
                <s.DetailBtn
                  onClick={() => setDiscountModalOpen(true)}
                  $active={profitOptions.profitRate !== 'A'}
                  $disabled={profitOptions.customBidCnt}
                >
                  <s.DetailBtnText>
                    {profitOptions.profitRate === 'A'
                      ? '감정가 대비'
                      : profitRateOptions.find(
                          (option) => option.value === profitOptions.profitRate,
                        )?.label}
                  </s.DetailBtnText>
                  <span>%</span>
                </s.DetailBtn>
                <s.CheckButtonLabel>
                  <input
                    type="checkbox"
                    checked={profitOptions.customBidCnt}
                    onChange={() =>
                      setProfitOptions((prev) => ({
                        ...prev,
                        customBidCnt: !prev.customBidCnt,
                        profitRate: 'A',
                        minBidCnt: '',
                        maxBidCnt: '',
                      }))
                    }
                  />
                  <img
                    src={
                      profitOptions.customBidCnt
                        ? '/assets/images/icon/algo-check-icon-on.svg'
                        : '/assets/images/icon/algo-check-icon-off.svg'
                    }
                    alt="유찰횟수 직접입력"
                  />
                  유찰횟수 직접입력
                </s.CheckButtonLabel>
                {profitOptions.customBidCnt && (
                  <s.BlockContent>
                    <s.InputBox>
                      <s.NumberInput
                        type="number"
                        pattern="[0-9]*"
                        placeholder="횟수"
                        value={profitOptions.minBidCnt}
                        onChange={(e) => {
                          // 입력값에서 숫자만 추출
                          let rawValue = e.target.value.replace(/[^0-9]/g, '');
                          if (rawValue.length > 9)
                            rawValue = rawValue.slice(0, 8);
                          setProfitOptions({
                            ...profitOptions,
                            minBidCnt: rawValue,
                          });
                        }}
                      />
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {profitOptions.minBidCnt && '회'} 이상
                      </span>
                    </s.InputBox>
                    <span style={{ padding: '0 4px' }}>~</span>
                    <s.InputBox>
                      <s.NumberInput
                        type="number"
                        pattern="[0-9]*"
                        placeholder="횟수"
                        value={profitOptions.maxBidCnt}
                        onFocus={() => {
                          setIsMaxPriceFocus(true);
                        }}
                        onBlur={() => setIsMaxPriceFocus(false)}
                        onChange={(e) => {
                          // 입력값에서 숫자만 추출
                          let rawValue = e.target.value.replace(/[^0-9]/g, '');
                          if (rawValue.length > 9)
                            rawValue = rawValue.slice(0, 8);
                          setProfitOptions({
                            ...profitOptions,
                            maxBidCnt: rawValue,
                          });
                        }}
                      />
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {profitOptions.maxBidCnt && '회'} 이하
                      </span>
                    </s.InputBox>
                  </s.BlockContent>
                )}
              </s.ColumnBox>
            </s.Block>
            <s.Block>
              <s.BlockLabel>
                <span>리스크 요인</span>
                {profitOptions.isRisk && (
                  <s.BlockLabelDescription>
                    위험감수는 최소 1개 이상 선택해주세요.
                  </s.BlockLabelDescription>
                )}
              </s.BlockLabel>
              <s.BlockContent>
                <s.RadioButtonLabel02
                  checked={!profitOptions.isRisk}
                  style={{ height: '40px' }}
                >
                  <input
                    type="radio"
                    name="revenue-type"
                    value="위험 회피"
                    checked={!profitOptions.isRisk}
                    onChange={() =>
                      setProfitOptions((prev) => ({
                        ...prev,
                        risk: [],
                        isRisk: false,
                      }))
                    }
                  />
                  위험 회피
                </s.RadioButtonLabel02>
                <s.RadioButtonLabel02
                  checked={profitOptions.isRisk}
                  style={{ height: '40px' }}
                >
                  <input
                    type="radio"
                    name="revenue-type"
                    value="위험 감수"
                    checked={profitOptions.isRisk}
                    onChange={() =>
                      setProfitOptions((prev) => ({
                        ...prev,
                        isRisk: true,
                      }))
                    }
                  />
                  위험 감수
                </s.RadioButtonLabel02>
              </s.BlockContent>
            </s.Block>

            {profitOptions.isRisk && (
              <s.Block>
                <s.BlockLabel>
                  <span>상세 요인</span>
                </s.BlockLabel>
                <s.DetailBtn
                  $active={profitOptions.risk.length > 0}
                  onClick={() => setRiskModalOpen(true)}
                >
                  {profitOptions.risk.length > 0 &&
                  profitOptions.risk.length !== riskSelectOptions.length - 1 ? (
                    <>
                      {
                        riskSelectOptions.find(
                          (o) => o.value === profitOptions.risk[0],
                        )?.label
                      }
                      {profitOptions.risk.length > 1 &&
                        ` + ${profitOptions.risk.length - 1}`}
                    </>
                  ) : (
                    <div style={{ color: 'rgba(55, 56, 60, 0.61)' }}>
                      상세리스크요인 선택
                    </div>
                  )}
                </s.DetailBtn>
              </s.Block>
            )}

            <s.Block>
              <s.BlockLabel>
                <span>매각 수익</span>
              </s.BlockLabel>
              <s.BlockContent>
                {profitStatusOptions.map((option) => (
                  <s.RadioButtonLabel02
                    key={option.value}
                    checked={profitOptions.profitStatus === option.value}
                  >
                    <input
                      type="radio"
                      name="investment-period"
                      value={option.value}
                      checked={profitOptions.profitStatus === option.value}
                      onChange={() => {
                        setProfitOptions((prev) => ({
                          ...prev,
                          profitStatus: option.value,
                        }));
                      }}
                    />
                    {option.label}
                    <s.RadioButtonLabelDescription>
                      {option.description}
                    </s.RadioButtonLabelDescription>
                  </s.RadioButtonLabel02>
                ))}
              </s.BlockContent>
            </s.Block>

            <s.Block>
              <s.BlockLabel>
                <span>시장가치</span>
              </s.BlockLabel>
              <s.BlockContent>
                {valueStatusOptions.map((option) => (
                  <s.RadioButtonLabel02
                    key={option.value}
                    checked={profitOptions.valueStatus === option.value}
                  >
                    <input
                      type="radio"
                      name="investment-period"
                      value={option.value}
                      checked={profitOptions.valueStatus === option.value}
                      onChange={() => {
                        setProfitOptions((prev) => ({
                          ...prev,
                          valueStatus: option.value,
                        }));
                      }}
                    />
                    {option.label}
                    <s.RadioButtonLabelDescription>
                      {option.description}
                    </s.RadioButtonLabelDescription>
                  </s.RadioButtonLabel02>
                ))}
              </s.BlockContent>
            </s.Block>

            <s.Block>
              <s.BlockLabel>
                <span>가격 신뢰도</span>
                <s.BlockLabelDescription>
                  높은 가격 신뢰도는 추천물건이 적을 수 있습니다.
                </s.BlockLabelDescription>
              </s.BlockLabel>
              <s.BlockContent>
                {prcReliabilityOptions.map((option) => (
                  <s.RadioButtonLabel02
                    key={option.value}
                    checked={profitOptions.prcReliability === option.value}
                  >
                    <input
                      type="radio"
                      name="investment-period"
                      value={option.value}
                      checked={profitOptions.prcReliability === option.value}
                      onChange={() => {
                        setProfitOptions((prev) => ({
                          ...prev,
                          prcReliability: option.value,
                        }));
                      }}
                    />
                    {option.label}
                    <s.RadioButtonLabelDescription>
                      {option.description}
                    </s.RadioButtonLabelDescription>
                  </s.RadioButtonLabel02>
                ))}
              </s.BlockContent>
            </s.Block>

            <s.Block>
              <s.BlockLabel>
                <span>사례분석</span>
                <s.BlockLabelDescription>
                  낙찰/매각 사례를 기반으로 수익성있는 물건을 추천합니다.
                </s.BlockLabelDescription>
              </s.BlockLabel>

              <s.DetailBtn
                onClick={() => setPurposeModalOpen(true)}
                $active={!!profitOptions.auctionDateYears}
              >
                {
                  auctionDateYearsOptions.find(
                    (option) => option.value === profitOptions.auctionDateYears,
                  )?.label
                }
              </s.DetailBtn>
            </s.Block>

            {riskModalOpen && (
              <RiskFactorModal
                onClose={() => setRiskModalOpen(false)}
                selected={profitOptions.risk}
                onApply={(selected: string[]) => {
                  setProfitOptions((prev) => ({
                    ...prev,
                    risk: selected,
                  }));
                  setRiskModalOpen(false);
                }}
              />
            )}

            {purposeModalOpen && (
              <PurposeModal
                setModalOn={setPurposeModalOpen}
                selected={profitOptions.auctionDateYears}
                onApply={(value) => {
                  setProfitOptions((prev) => ({
                    ...prev,
                    auctionDateYears: value,
                  }));
                }}
              />
            )}

            {discountModalOpen && (
              <DiscountModal
                selected={profitOptions.profitRate}
                onClose={() => setDiscountModalOpen(false)}
                onApply={(value) => {
                  setProfitOptions((prev) => ({
                    ...prev,
                    profitRate: value,
                  }));
                }}
              />
            )}
            {costIncludeModalOpen && (
              <CostIncludeModal
                onClose={() => setCostIncludeModalOpen(false)}
              />
            )}
          </s.SelectMain>
        </>
      )}
      <SelectedOptions tab="수익" />
    </s.PatternBox>
  );
};

export default CorePattern;
